// Quarter configuration and pricing mappings
// Single source of truth for all quarter-related logic

// Mapping of quarters to product price keys in productDb
export const quarterMapping = {
  '2026-Q1': 'pricingQ12026',
  '2026-Q2': 'pricingQ22026',
  '2026-Q3': 'pricingQ32026',
  '2026-Q4': 'pricingQ42026',
  '2027-Q1': 'pricingQ12027',
  '2027-Q2': 'pricingQ22027',
  '2027-Q3': 'pricingQ32027',
  '2027-Q4': 'pricingQ42027',
  '2028-Q1': 'pricingQ12028',
  '2028-Q2': 'pricingQ22028',
  '2028-Q3': 'pricingQ32028',
  '2028-Q4': 'pricingQ42028'
};

// Derives the current quarter from the system date (Jan–Mar=Q1, Apr–Jun=Q2, Jul–Sep=Q3, Oct–Dec=Q4)
const deriveCurrentQuarter = () => {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${quarter}`;
};

// Quarter availability configuration
export const QUARTER_CONFIG = {
  OVERRIDE_QUARTER: null, // Set to e.g. '2026-Q3' to force a specific quarter; null = auto-detect
  // Full catalog of quarters we have pricing for. Past quarters are NOT removed
  // by hand — getAvailableQuarters() filters them out by system date.
  AVAILABLE_QUARTERS: [
    '2026-Q2',
    '2026-Q3',
    '2026-Q4',
    '2027-Q1',
    '2027-Q2',
    '2027-Q3',
    '2027-Q4',
    '2028-Q1',
    '2028-Q2',
    '2028-Q3',
    '2028-Q4'
  ]
};

// Helper functions for quarter management
export const getCurrentQuarter = () => QUARTER_CONFIG.OVERRIDE_QUARTER || deriveCurrentQuarter();
// Returns only the current quarter and future ones — past quarters are pruned
// automatically based on the system date, so the dropdown self-maintains as time
// rolls forward (e.g. once we enter Q4, Q3 drops off without a code change).
export const getAvailableQuarters = () => {
  const currentQuarter = getCurrentQuarter();
  return QUARTER_CONFIG.AVAILABLE_QUARTERS.filter(
    (quarter) => compareQuarters(quarter, currentQuarter) >= 0
  );
};
export const isQuarterAvailable = (quarter) => QUARTER_CONFIG.AVAILABLE_QUARTERS.includes(quarter);

// Get the pricing key for a given quarter
export const getQuarterPricingKey = (quarter) => quarterMapping[quarter];

/**
 * Compare two quarters in YYYY-QN format
 * @param {string} quarterA - Format: "2026-Q3"
 * @param {string} quarterB - Format: "2025-Q4"
 * @returns {number} -1 if A < B, 0 if equal, 1 if A > B
 */
export const compareQuarters = (quarterA, quarterB) => {
  if (!quarterA || !quarterB) return 0;

  const parseQuarter = (q) => {
    const [year, quarter] = q.split('-Q');
    return { year: parseInt(year), quarter: parseInt(quarter) };
  };

  const a = parseQuarter(quarterA);
  const b = parseQuarter(quarterB);

  if (a.year !== b.year) return a.year - b.year;
  return a.quarter - b.quarter;
};

/**
 * Check if a SKU is available for the selected delivery quarter
 * @param {string|undefined} skuAvailableFrom - SKU's availableFrom field
 * @param {string} selectedQuarter - User's selected delivery quarter
 * @returns {boolean} True if SKU should be shown
 */
export const isSkuAvailable = (skuAvailableFrom, selectedQuarter) => {
  // If no availableFrom specified, SKU is always available
  if (!skuAvailableFrom) return true;

  // Validate quarter format is in our system
  if (!isQuarterAvailable(selectedQuarter)) {
    console.warn(`Invalid quarter selected: ${selectedQuarter}`);
    return false;
  }

  // SKU is available if selectedQuarter >= availableFrom
  return compareQuarters(selectedQuarter, skuAvailableFrom) >= 0;
};


// Validate that all available quarters have pricing mappings
export const validateQuarterPricing = () => {
  const missingQuarters = QUARTER_CONFIG.AVAILABLE_QUARTERS.filter(
    quarter => !quarterMapping[quarter]
  );
  if (missingQuarters.length > 0) {
    console.warn('Missing pricing mappings for quarters:', missingQuarters);
  }
  return missingQuarters.length === 0;
};

// Validate on module load in development
// if (process.env.NODE_ENV === 'development') {
//   validateQuarterPricing();
// }